<template>
    <div
        class="accordion-item"
    >
        <button
            class="accordion-toggle"
            @click="toggleAccordion"
        >
            <h2 class="accordion-heading h5" :class="{'is-open': accordionOpen}">{{ title }}</h2>
            <div class="icon accordion-toggle-icon">
                <div class="icon-list">
                    <DSIcon
                        class="open-icons"
                        code="subtract"
                        :class="{ 'active': accordionOpen }"
                    />
                    <DSIcon
                        class="close-icons"
                        code="add"
                        :class="{ 'active': !accordionOpen }"
                    />
                </div>
            </div>
        </button>
        <DSHeightTransition :open="accordionOpen">
            <div class="content cards">
                <component
                    :is="cardElement(card)"
                    v-for="card in cards"
                    :key="card.id"
                    :to="cardLinkUrl(card)"
                    :target="cardLinkTarget(card)"
                    class="cl-card"
                >
                    <DSFrontifyAsset
                        v-if="card.image"
                        fill="cover"
                        :asset="card.image"
                        class="cl-card-image"
                    />
                    <div class="card-details">
                        <h2 class="cl-card-title">{{ card.title }}</h2>
                        <DSCFText class="cl-card-description" :content="card.text" />
                        <DSFontIcon v-if="cardLink(card)" class="card-icon" code="arrow-right" />
                    </div>
                </component>
            </div>
        </DSHeightTransition>
    </div>
</template>
<script setup>
import { ref, resolveComponent } from 'vue';
import DSFontIcon from '~dsc/components/Components/DSFontIcon.vue';

const props = defineProps({
    cards: {
        type: [ Array ],
        required: false,
        default: () => [],
    },
    title: {
        type: [ String, null ],
        required: false,
        default: '',
    },
    isOpen: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const accordionOpen = ref(props.isOpen ?? false);

const toggleAccordion = () => accordionOpen.value = !accordionOpen.value;

const cardElement = card => cardLink(card) ? resolveComponent('NuxtLink') : 'div';
const cardLink = card => card?.links?.length > 0 ? card.links[0] : false;
const cardLinkUrl = card => {
    const link = cardLink(card);

    return link?.url?.value ?? link?.url?.url;
};
const cardLinkTarget = card => cardLink(card)?.openInNewTab ? '_blank' : '_self';

</script>
<style scoped>
.accordion-item {
  will-change: height;
}
.accordion-item:focus-visible {
  outline: var(--color-base-grey-500) auto 1px;
}

.accordion-toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 0;
  cursor: pointer;
  text-align: left;
}
.accordion-toggle:hover .accordion-heading {
  color: var(--color-primary-900);
}
.accordion-toggle:hover .icon :deep(svg) {
  fill: var(--color-primary-900);
}

.accordion-heading {
  text-transform: uppercase;
  margin-bottom: 0;
}
.accordion-heading.is-open {
  color: var(--color-primary-900);
}

.accordion-toggle-icon {
  color: var(--color-base-grey-900);
}

.icon-list {
  display: inline-flex;
  justify-content: center;
  position: relative;
  width: 2rem;
  height: 2rem;
}
.icon-list .icon {
  transition: opacity 0.5s ease-out;
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  opacity: 0;
}
.icon-list .icon.active {
  opacity: 1;
}

.open-icons :deep(svg) {
  fill: var(--color-primary-900);
}

.close-icons {
  color: var(--color-base-grey-500);
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  grid-column-start: 1;
  grid-column-end: 3;
  margin-bottom: 2rem;
}

.cl-card {
  display: flex;
  flex-direction: column;
  background-color: var(--cl-product-card, #eef1f6);
  padding: 1.5rem;
  border-radius: 1.75rem;
}

.cl-card-image {
  min-height: 200px;
  flex-basis: 200px;
  border-radius: 0.75rem;
  margin-bottom: 2rem;
}

.card-details {
  display: grid;
  grid-template-columns: 1fr 3.5rem;
}

.cl-card-title {
  grid-column-start: 1;
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
}

.cl-card-description {
  grid-column-start: 1;
}

.card-icon {
  grid-column-start: 2;
  grid-row-start: 1;
  grid-row-end: 3;
  align-content: center;
  text-align: center;
  font-size: 1.25rem;
}

@media (min-width: 992px) {
  .cl-card-title {
    font-size: 1.625rem;
  }
}
</style>
